import { AdPlatformData } from "../../../interfaces/AdStrategy";

export const ALL_PLATFORMS = {
  googleAds: {
    name: "Google Ads",
    configuration: {
      campaign: {
        type: "Search and Display Ads",
        objective: "Lead Generation",
        networkTargeting: [
          "Google Search Network",
          "Google Display Network",
          "YouTube",
        ],
      },
      targeting: {
        keywords: ["ai workflow automation", "enterprise software solutions"],
        audiences: ["C-level executives", "IT Directors"],
        demographics: ["Ages 30-55", "North America, Europe"],
      },
      creativeRecommendations: {
        formatTypes: ["Responsive Search Ads", "Banner Ads", "Video Ads"],
        dimensions: ["300x250", "728x90", "160x600"],
        copyLength: "Headlines: 30 characters, Descriptions: 90 characters",
      },
    },
  },
  facebookAds: {
    name: "Facebook Ads",
    configuration: {
      campaign: {
        objective: "Lead Generation",
        buyingType: "Auction",
        optimizationGoal: "Leads",
      },
      targeting: {
        interests: ["AI and machine learning", "Enterprise software"],
        behaviors: ["Business decision-making", "Technology adoption"],
        demographics: ["C-level executives", "VP-level professionals"],
      },
      creativeRecommendations: {
        formatTypes: ["Single Image", "Video", "Carousel"],
        dimensions: ["1200x628", "1080x1080"],
        copyLength: "Brief (up to 90 characters)",
      },
    },
  },
  youtubeAds: {
    name: "YouTube Ads",
    configuration: {
      campaign: {
        type: "Video Advertising",
        objective: "Lead Generation",
        networkTargeting: [
          "YouTube Search",
          "YouTube Videos",
          "Video Partners",
        ],
      },
      targeting: {
        videoTopics: ["Technology", "Business", "Innovation"],
        audiences: ["Business Decision Makers", "Tech Enthusiasts"],
        demographics: ["Ages 25-54", "Professional Interests"],
        placements: ["Tech Channels", "Business News"],
      },
      creativeRecommendations: {
        formatTypes: ["In-Stream Ads", "Discovery Ads", "Bumper Ads"],
        dimensions: ["16:9 aspect ratio", "1920x1080"],
        copyLength: "Skippable: 3+ min, Bumper: 6 sec, Headlines: 15 chars",
      },
    },
  },
  instagramAds: {
    name: "Instagram Ads",
    configuration: {
      campaign: {
        objective: "Lead Generation",
        buyingType: "Auction",
        optimizationGoal: "Leads",
      },
      targeting: {
        interests: ["Business Technology", "Digital Innovation"],
        behaviors: ["B2B Technology Engagement", "Professional Networking"],
        demographics: ["Business Decision Makers", "Tech Professionals"],
      },
      creativeRecommendations: {
        formatTypes: ["Stories", "Reels", "Feed Posts", "Carousel"],
        dimensions: ["1080x1920", "1080x1080", "1080x1350"],
        copyLength: "Caption: 125 characters, Stories: 125 characters",
      },
    },
  },
  linkedinAds: {
    name: "LinkedIn Ads",
    configuration: {
      campaign: {
        objective: "Lead Generation",
        format: "Sponsored Content",
      },
      targeting: {
        jobTitles: ["Chief Technology Officer", "VP of Operations"],
        jobFunctions: ["Technology", "Operations"],
        industries: ["Technology", "Finance"],
      },
      creativeRecommendations: {
        formatTypes: ["Sponsored Content", "Message Ads"],
        dimensions: ["1200x627"],
        copyLength: "150-200 words",
      },
    },
  },
  redditAds: {
    name: "Reddit Ads",
    configuration: {
      campaign: {
        objective: "Lead Generation",
        bidType: "CPC",
      },
      targeting: {
        subreddits: ["r/technology", "r/business"],
        interests: ["Technology", "Business"],
        demographics: ["Professional audience"],
      },
      creativeRecommendations: {
        formatTypes: ["Image Ads", "Video Ads"],
        dimensions: ["1080x1080"],
        copyLength: "Short headlines (max 80 characters)",
      },
    },
  },
  snapchatAds: {
    name: "Snapchat Ads",
    configuration: {
      campaign: {
        objective: "Lead Generation",
        status: "active",
      },
      targeting: {
        demographics: ["Age: 25-44", "Professional"],
        interests: ["Technology", "Business"],
        customAudiences: ["Previous webinar attendees"],
      },
      creativeRecommendations: {
        formatTypes: ["Snap Ads", "Story Ads"],
        dimensions: ["1080x1920"],
        copyLength: "Up to 90 characters",
      },
    },
  },
  tiktokAds: {
    name: "TikTok Ads",
    configuration: {
      campaign: {
        objective: "Lead Generation",
        promotionType: "Product Awareness",
      },
      targeting: {
        interests: ["Technology", "Business"],
        behaviors: ["Professional content engagement"],
        demographics: ["Business professionals"],
      },
      creativeRecommendations: {
        formatTypes: ["Short-form videos"],
        dimensions: ["9:16 vertical format"],
        copyLength: "Brief (10-15 words)",
      },
    },
  },
  xAds: {
    name: "X Ads",
    configuration: {
      campaign: {
        objective: "Lead Generation",
        format: "Promoted Tweets",
        promotionType: "Website Traffic",
      },
      targeting: {
        interests: ["Technology", "Business", "Innovation"],
        behaviors: ["Tech News Engagement", "B2B Decision Making"],
        demographics: ["Business Professionals", "Tech Industry"],
        keywords: ["enterprise tech", "digital transformation"],
      },
      creativeRecommendations: {
        formatTypes: ["Image Tweets", "Video Tweets", "Text Tweets"],
        dimensions: ["1200x675", "16:9 video"],
        copyLength: "Tweet text: 280 characters",
      },
    },
  },
} as const;

// Default platform configuration matching the AdPlatform interface
export const DEFAULT_PLATFORM_CONFIG: AdPlatformData = {
  name: "",
  enabled: false,
  configuration: {
    campaign: {
      objective: "Lead Generation",
      type: "",
      goals: [],
      networkTargeting: [],
    },
    targeting: {
      keywords: [],
      audiences: [],
      demographics: [],
      interests: [],
      behaviors: [],
      locations: ["North America", "Europe"],
    },
    creativeRecommendations: {
      formatTypes: [],
      dimensions: [],
      copyLength: "",
    },
  },
  metrics: {
    budget: 0,
    spend: 0,
    impressions: 0,
    clicks: 0,
    conversions: 0,
    costPerClick: 0,
    costPerConversion: 0,
    conversionRate: 0,
  },
};

export const MOCK_STRATEGY_1 = {
  _id: "67a2cdae61ec0054bc6dc6cb",
  project_id: "679eed87bc51456144f11414",
  icp_id: "67a17c3a167c1b68b0d20ed5",
  name: "ad-strategy-p:679eed87bc51456144f11414-icp:67a17c3a167c1b68b0d20ed5",
  status: "draft",
  platforms: {
    googleAds: {
      campaign: {
        type: "Search and Display",
        goals: ["Lead Generation", "Brand Awareness", "Increase Web Traffic"],
        networkTargeting: ["Google Search Network", "Google Display Network"],
        analyticsIntegration: {
          trackingSetup: "Google Analytics and Google Tag Manager",
          conversionGoals: [
            "Sign-ups for webinars",
            "Requests for product demos",
            "Contact form submissions",
          ],
          attributionModel: "Data-Driven Attribution",
        },
      },
      targeting: {
        keywords: {
          primary: [
            "AI workflow automation",
            "enterprise automation solutions",
            "real-time data analytics",
            "digital transformation in finance",
            "automating HR processes",
          ],
          negative: [
            "cheap automation tools",
            "DIY automation",
            "small business automation",
          ],
          competitors: [
            "CompetitorA automation solutions",
            "CompetitorB workflow software",
            "CompetitorC enterprise software",
          ],
        },
        audiences: [
          "Technology Decision Makers",
          "Finance Executives",
          "Healthcare Operations Leaders",
        ],
        demographics: [
          "C-suite professionals",
          "Senior decision-makers in enterprises",
        ],
        locations: {
          included: ["North America", "Europe"],
          excluded: ["Asia"],
          radiusTargeting: "50 miles around major metropolitan areas",
        },
      },
      scheduling: {
        timeZone: "America/New_York",
        activePeriods: ["Monday to Friday"],
        peakEngagementHours: ["10 AM - 12 PM", "2 PM - 4 PM"],
        dayparting: {
          weekday: ["10 AM - 12 PM", "2 PM - 4 PM"],
          weekend: ["Not Applicable"],
          customRules: [
            "Target more aggressively during Q1 and Q4 fiscal budgets review periods",
          ],
        },
      },
      metrics: {
        estimatedReach: 200000,
        suggestedBudget: 50000,
        estimatedCPC: 15,
        targetROAS: 500,
        budgetDistribution: {
          search: 60,
          display: 30,
          video: 10,
        },
      },
      enabled: true,
    },
    facebookAds: {
      campaign: {
        objective: "Conversions",
        buyingType: "Auction",
        optimizationGoal: "Link Clicks",
        analyticsSetup: {
          pixelConfiguration:
            "Standard Events for Purchase, Add to Cart and Complete Registration",
          customConversions: ["Completed Purchase", "Lead Form Submission"],
          catalogIntegration: "Product Catalog synchronized with Facebook Shop",
        },
      },
      targeting: {
        interests: [
          "E-commerce",
          "Online Shopping",
          "Small Business Owners",
          "Digital Marketing",
          "Social Media Marketing",
        ],
        behaviors: ["Engaged Shoppers", "Frequent Online Shoppers"],
        demographics: ["Age 25-45", "College Graduates", "Professionals"],
        locations: {
          included: ["United States", "Canada"],
          excluded: ["Non-English speaking countries"],
          radiusTargeting: ["10 miles around major metropolitan areas"],
        },
      },
      metrics: {
        estimatedReach: 150000,
        suggestedBudget: 500,
        estimatedCPC: 0.5,
        platformDistribution: {
          facebook: 60,
          instagram: 30,
          stories: 5,
          reels: 5,
        },
      },
      enabled: true,
    },
    youtubeAds: {
      campaign: {
        objective:
          "Generate leads for AI-powered workflow automation platform targeting enterprise decision-makers.",
        format: "TrueView In-stream Ads",
        videoTypes: ["Explainer videos", "Customer testimonials", "Webinars"],
      },
      targeting: {
        audiences: [
          "Chief Technology Officer",
          "VP of Operations",
          "Head of Digital Transformation",
          "IT Directors",
          "CFOs",
        ],
        placements: [
          "Videos on enterprise technology",
          "Webinars focused on digital transformation",
          "Industry events and conference videos",
        ],
        topics: [
          "AI automation",
          "Workflow optimization",
          "Digital transformation",
          "Enterprise software solutions",
        ],
      },
      metrics: {
        estimatedReach: 400000,
        suggestedBudget: 200000,
        estimatedCPV: 0.05,
      },
      enabled: false,
    },
    instagramAds: {
      campaign: {
        objective: "Lead Generation",
        buyingType: "Auction",
        optimizationGoal: "Lead Form Submissions",
        analyticsSetup: {
          pixelConfiguration: "Standard Event Tracking on Lead Completion",
          customConversions: ["Lead Submission", "Book a Demo"],
          catalogIntegration: "Product Catalog for Retargeting",
        },
      },
      targeting: {
        interests: [
          "Entrepreneurship",
          "Small Business",
          "Digital Marketing",
          "E-commerce",
          "Technology",
        ],
        behaviors: [
          "Business Page Admins",
          "Engaged Shoppers",
          "Frequent Mobile Users",
        ],
        demographics: [
          "Ages 25-45",
          "Education: College Graduates",
          "Household Income: Top 25%",
        ],
      },
      metrics: {
        estimatedReach: 200000,
        suggestedBudget: 1500,
        estimatedCPC: 2.5,
      },
      enabled: true,
    },
    tiktokAds: {
      campaign: {
        objective:
          "Generate Awareness and Lead Generation for AI-powered Workflow Automation Platform",
        promotionType: "In-Feed Ads",
      },
      targeting: {
        interests: [
          "AI automation",
          "Data analytics in enterprise",
          "Digital transformation",
          "Workflow optimization",
          "Enterprise software solutions",
        ],
        behaviors: [
          "Frequent engagement with professional content",
          "Attended technology summits or seminars",
        ],
        demographics: [
          "C-Suite executives",
          "VPs of Operations",
          "IT Directors",
          "CFOs",
          "Digital Transformation leaders",
        ],
      },
      metrics: {
        estimatedReach: 500000,
        suggestedBudget: 10000,
        estimatedCPC: 2.5,
      },
      enabled: true,
    },
    linkedinAds: {
      campaign: {
        objective: "Lead Generation",
        format: "Sponsored Content",
        analyticsSetup: {
          insightTag: "Enabled",
          conversionTracking: ["Lead Submission", "Website Visit"],
          leadGenForms: "Enabled",
        },
      },
      targeting: {
        jobTitles: [
          "Marketing Manager",
          "Business Development Executive",
          "Sales Director",
        ],
        jobFunctions: ["Marketing", "Sales", "Business Development"],
        industries: ["Technology", "Finance", "Healthcare"],
        companySize: ["51-200", "201-500", "501-1000"],
      },
      metrics: {
        estimatedReach: 25000,
        suggestedBudget: 1500,
        estimatedCPC: 3.25,
        formatDistribution: {
          sponsored: 70,
          message: 20,
          dynamic: 10,
        },
      },
      enabled: true,
    },
    snapchatAds: {
      campaign: {
        objective:
          "Increase brand awareness and lead generation for AI-powered workflow automation platform targeting decision-makers in enterprises.",
        status: "active",
      },
      targeting: {
        demographics: [
          "Age: 30-55",
          "Gender: All",
          "Education: Bachelor's Degree or higher",
          "Experience: 10+ years in relevant fields",
        ],
        interests: [
          "AI automation",
          "Workflow optimization",
          "Enterprise software solutions",
          "Digital transformation",
        ],
      },
      metrics: {
        estimatedReach: 1500000,
        suggestedBudget: 50000,
        estimatedCPC: 1.5,
      },
      enabled: true,
    },
    xAds: {
      campaign: {
        objective: "Lead Generation and Engagement",
        format: "Promoted Tweets & Takeovers",
        tweetTypes: [
          "Promoted Tweets",
          "Video Tweets",
          "Polls",
          "Image/Carousel Ads",
        ],
      },
      targeting: {
        followers: [
          "Prominent AI thought leaders",
          "Enterprise software analysts",
          "Automation industry experts",
          "Tech futurists",
          "Business process optimization specialists",
        ],
        interests: [
          "AI automation",
          "Workflow optimization",
          "Enterprise software solutions",
          "Data analytics in enterprise",
          "Digital transformation",
        ],
      },
      metrics: {
        estimatedReach: 50000,
        suggestedBudget: 20000,
        estimatedCPE: 5,
      },
      enabled: true,
    },
    redditAds: {
      campaign: {
        objective:
          "Lead Generation through targeted engagement with decision-makers in enterprise sectors.",
        bidType: "CPC",
      },
      targeting: {
        subreddits: [
          "technology",
          "DataIsBeautiful",
          "FinTech",
          "HealthcareInnovation",
          "MachineLearning",
          "AIandRobotics",
          "dataanalysis",
          "DigitalTransformation",
        ],
        interests: [
          "AI automation",
          "Workflow optimization",
          "Enterprise software solutions",
          "Data analytics in enterprise",
          "Digital transformation",
        ],
      },
      metrics: {
        estimatedReach: 50000,
        suggestedBudget: 2500,
        estimatedCPC: 2,
      },
      enabled: true,
    },
  },
  crossPlatform: {
    budgetAllocation: {
      distribution: [
        {
          platform: "Google Ads",
          percentage: 25,
          reasoning:
            "High volume of search queries related to enterprise software and automation which aligns with customer intent.",
          kpis: ["CPC", "ROAS", "Conversion Rate"],
        },
        {
          platform: "LinkedIn",
          percentage: 30,
          reasoning:
            "Direct access to decision-makers in enterprises, targeting specific job titles and industries relevant to our ICP.",
          kpis: ["Lead Generation Rate", "CPC", "Conversion Rate"],
        },
        {
          platform: "YouTube",
          percentage: 15,
          reasoning:
            "Growing trend of consuming video content related to AI and automation, providing an opportunity to showcase product features.",
          kpis: ["CPV", "Engagement Rate", "Lead Generation Rate"],
        },
        {
          platform: "Facebook & Instagram",
          percentage: 10,
          reasoning:
            "To retarget users who engage with other platforms, raising awareness and facilitating lead generation.",
          kpis: ["CTR", "Conversion Rate", "Cost Per Lead"],
        },
        {
          platform: "X (formerly Twitter)",
          percentage: 5,
          reasoning:
            "Engagement with tech-specific trends and industry conversations to drive awareness.",
          kpis: ["Engagement Rate", "CPE", "Lead Generation Rate"],
        },
        {
          platform: "Reddit",
          percentage: 5,
          reasoning:
            "Focus on niche communities that discuss technological advancements and automation, allowing targeted engagement.",
          kpis: ["CPC", "Conversion Rate", "Engagement Rate"],
        },
        {
          platform: "TikTok",
          percentage: 5,
          reasoning:
            "Emergence of B2B interest on short video platforms, targeting younger decision-makers in tech.",
          kpis: ["CPM", "Engagement Rate", "Lead Generation Rate"],
        },
        {
          platform: "Snapchat",
          percentage: 5,
          reasoning:
            "Engaging with tech-savvy professionals through immersive content.",
          kpis: ["CPC", "Engagement Rate", "Lead Generation Rate"],
        },
      ],
    },
    campaignSynergy: {
      crossPlatformRetargeting: [
        "Google Ads to retarget visitors from LinkedIn ads",
        "YouTube viewers retargeted on Facebook and Instagram",
        "Engaged users on Reddit targeted with LinkedIn lead ads",
      ],
      attributionModel:
        "Multi-Touch Attribution, focusing on assisted conversions",
      customerJourneyMapping: [
        "Awareness via Google and LinkedIn ads",
        "Consideration through webinars and case studies on YouTube and Facebook",
        "Decision aided by direct demos and outreach from retargeted ads",
      ],
    },
    analyticsIntegration: {
      primaryTrackingPlatform: "Google Analytics & HubSpot",
      crossPlatformPixels: [
        "Facebook Pixel",
        "LinkedIn Insight Tag",
        "Google Ads Conversion Tracking",
      ],
      unifiedReporting: {
        metrics: [
          "Cost Per Acquisition",
          "Lead Conversion Rate",
          "Traffic Sources",
          "User Engagement",
        ],
        dashboards: ["Google Data Studio", "HubSpot Marketing Dashboard"],
      },
    },
  },
  influencerMarketing: {
    strategy: {
      objective:
        "Drive brand awareness and lead generation through authentic industry expert partnerships",
      platformMix: ["LinkedIn", "YouTube", "Medium"],
      campaignTypes: ["Product Reviews", "Webinars", "Case Studies"],
      contentApproach:
        "Collaborate with influencers to create educational content highlighting AI automation benefits",
    },
    influencerTiers: {
      micro: {
        followerRange: "1,000 - 10,000",
        quantity: 15,
        estimatedReach: 150000,
        budgetAllocation: 30000,
      },
      macro: {
        followerRange: "100,000 - 1,000,000",
        quantity: 5,
        estimatedReach: 500000,
        budgetAllocation: 100000,
      },
      categoryExperts: {
        niches: [
          "AI and Machine Learning",
          "Enterprise Software",
          "Digital Transformation",
        ],
        platforms: ["LinkedIn", "YouTube"],
        selectionCriteria: [
          "Industry relevance",
          "Engagement rate",
          "Content quality",
        ],
      },
    },
    contentGuidelines: {
      formats: ["Webinars", "Video Tutorials", "Blog Posts", "Infographics"],
      keyMessages: [
        "Reduce manual workflow time by 75%",
        "Achieve 300% ROI within first year",
        "Seamless integration with existing systems",
      ],
      brandValues: ["Innovation", "Efficiency", "Customer Success"],
      creativeControl:
        "Influencers will have creative freedom with brand alignment requirements",
    },
    metrics: {
      primaryKPIs: [
        "Engagement Rate",
        "Lead Generation",
        "Content Shares",
        "Webinar Attendance",
      ],
      engagementBenchmarks: {
        micro: 5.0,
        macro: 2.5,
      },
      conversionGoals: [
        "Product Demos",
        "Webinar Sign-ups",
        "Content Downloads",
      ],
      attributionModel:
        "Last-click attribution for lead generation and engagement tracking",
    },
  },
  affiliateMarketing: {
    program: {
      structure: "Performance-based program with tiered commission structure",
      partnerTiers: [
        {
          name: "Silver",
          criteria: ["Minimum 5 sales/month", "Active social media"],
          benefits: ["Basic promotional materials", "Monthly reports"],
          commissionRates: {
            firstSale: 10,
            recurring: 5,
            bonuses: ["2% extra per 10 sales"],
          },
        },
        {
          name: "Gold",
          criteria: ["Minimum 15 sales/month", "Established presence"],
          benefits: ["Higher commissions", "Dedicated manager"],
          commissionRates: {
            firstSale: 15,
            recurring: 7,
            bonuses: ["5% extra per 20 sales"],
          },
        },
      ],
      paymentTerms: {
        frequency: "Monthly",
        minimumPayout: 100,
        methods: ["Bank Transfer", "PayPal"],
      },
    },
    partnerTypes: {
      contentCreators: {
        targetVerticals: ["Technology", "Finance", "Healthcare"],
        platforms: ["YouTube", "Blogs", "Podcasts"],
        recruitmentStrategy: ["Social outreach", "Influencer campaigns"],
      },
      industryExperts: {
        niches: ["AI/Automation", "Enterprise Software"],
        qualifications: ["Industry certifications", "Thought leadership"],
        outreachPlan: ["Industry events", "LinkedIn outreach"],
      },
    },
    metrics: {
      kpis: ["Sales generated", "Conversion rates", "Partner engagement"],
      revenueTargets: {
        quarterly: 500000,
        annual: 2000000,
      },
      partnerMetrics: ["Sales per partner", "Average order value"],
      ltv: "Estimated $150,000 per customer",
    },
  },
  growthStrategy: {
    core: {
      northStarMetric: "Monthly Active Users (MAU)",
      growthLevers: [
        "Viral loops",
        "Product-led growth",
        "Community engagement",
      ],
      experimentationFramework: "Lean Startup methodology",
    },
    acquisition: {
      viralLoops: [
        {
          type: "Referral Program",
          mechanism: "Credits for successful referrals",
          incentives: ["Subscription discounts", "Premium features"],
          expectedK: 3,
        },
      ],
      productLedGrowth: {
        hooks: ["Feature tutorials", "Gamification"],
        ahaMoments: ["Quick workflow automation", "Real-time insights"],
        frictionPoints: ["Complex onboarding", "Integration challenges"],
      },
      communityGrowth: {
        platforms: ["LinkedIn groups", "Industry forums", "Slack"],
        engagementTactics: ["Expert webinars", "Content contests"],
        scalingStrategy: "Industry influencer partnerships",
      },
    },
    retention: {
      hooks: ["Feature updates", "Usage reports"],
      reengagementTriggers: ["Inactive reminders", "Feature notifications"],
      loyaltyProgram: {
        structure: "Subscription-length tiers",
        rewards: ["Renewal discounts", "Beta access"],
        tiers: ["Silver: 1yr", "Gold: 2yr", "Platinum: 3yr+"],
      },
    },
    metrics: {
      primaryKPIs: ["Activation rate", "Retention rate", "NPS"],
      targetROI: "300% within first year",
      growthProjections: {
        monthly: "15% user growth",
        annual: "200% revenue growth",
      },
    },
  },
  createdAt: "2025-02-05T02:32:14.608Z",
  updatedAt: "2025-02-05T02:32:14.608Z",
};

// Clone mock strategy for FinTech focus with updated fields
export const MOCK_STRATEGY_2 = {
  ...MOCK_STRATEGY_1,
  _id: "2",
  name: "Q2 2025 FinTech Expansion",
  platforms: {
    ...MOCK_STRATEGY_1.platforms,
    linkedinAds: {
      ...MOCK_STRATEGY_1.platforms.linkedinAds,
      enabled: true,
      metrics: {
        budget: 8000,
        spend: 7200,
        impressions: 150000,
        clicks: 3000,
        conversions: 90,
        costPerClick: 2.4,
        costPerConversion: 80,
        conversionRate: 0.03,
      },
    },
  },
  crossPlatform: {
    ...MOCK_STRATEGY_1.crossPlatform,
    budgetAllocation: {
      ...MOCK_STRATEGY_1.crossPlatform.budgetAllocation,
      distribution: [
        {
          platform: "LinkedIn",
          percentage: 40,
          reasoning:
            "High concentration of FinTech decision-makers on LinkedIn",
          kpis: ["Lead Generation Rate", "CPC", "Conversion Rate"],
        },
        ...MOCK_STRATEGY_1.crossPlatform.budgetAllocation.distribution.slice(1),
      ],
    },
  },
  influencerMarketing: {
    ...MOCK_STRATEGY_1.influencerMarketing,
    strategy: {
      ...MOCK_STRATEGY_1.influencerMarketing.strategy,
      platformMix: ["LinkedIn", "YouTube", "FinTech blogs"],
      campaignTypes: [
        "FinTech case studies",
        "Industry webinars",
        "Expert interviews",
      ],
    },
  },
  affiliateMarketing: {
    ...MOCK_STRATEGY_1.affiliateMarketing,
    partnerTypes: {
      ...MOCK_STRATEGY_1.affiliateMarketing.partnerTypes,
      industryExperts: {
        niches: ["FinTech", "Banking", "Financial Services"],
        qualifications: ["Financial certifications", "Banking experience"],
        outreachPlan: ["FinTech events", "Banking conferences"],
      },
    },
  },
  growthStrategy: {
    ...MOCK_STRATEGY_1.growthStrategy,
    core: {
      ...MOCK_STRATEGY_1.growthStrategy.core,
      northStarMetric: "Financial Transactions Automated",
      growthLevers: [
        "Banking integrations",
        "FinTech partnerships",
        "Compliance features",
      ],
    },
  },
};

// Clone mock strategy for Healthcare focus
export const MOCK_STRATEGY_3 = {
  ...MOCK_STRATEGY_1,
  _id: "3",
  name: "Q3 2025 Healthcare Initiative",
  platforms: {
    ...MOCK_STRATEGY_1.platforms,
    youtubeAds: {
      ...MOCK_STRATEGY_1.platforms.youtubeAds,
      enabled: true,
      metrics: {
        budget: 10000,
        spend: 9500,
        impressions: 800000,
        clicks: 40000,
        conversions: 200,
        costPerClick: 0.24,
        costPerConversion: 47.5,
        conversionRate: 0.005,
      },
    },
  },
  crossPlatform: {
    ...MOCK_STRATEGY_1.crossPlatform,
    budgetAllocation: {
      ...MOCK_STRATEGY_1.crossPlatform.budgetAllocation,
      distribution: [
        {
          platform: "YouTube",
          percentage: 35,
          reasoning:
            "Video content highly effective for healthcare solution demonstrations",
          kpis: ["View Rate", "Lead Generation Rate", "Cost Per View"],
        },
        ...MOCK_STRATEGY_1.crossPlatform.budgetAllocation.distribution.slice(1),
      ],
    },
  },
  influencerMarketing: {
    ...MOCK_STRATEGY_1.influencerMarketing,
    strategy: {
      ...MOCK_STRATEGY_1.influencerMarketing.strategy,
      platformMix: ["LinkedIn", "Medical journals", "Healthcare blogs"],
      campaignTypes: [
        "Clinical workflow studies",
        "Healthcare webinars",
        "Compliance guides",
      ],
    },
  },
  affiliateMarketing: {
    ...MOCK_STRATEGY_1.affiliateMarketing,
    partnerTypes: {
      ...MOCK_STRATEGY_1.affiliateMarketing.partnerTypes,
      industryExperts: {
        niches: ["Healthcare IT", "Medical Operations", "Clinical Services"],
        qualifications: ["Healthcare certifications", "Clinical experience"],
        outreachPlan: ["Medical conferences", "Healthcare networks"],
      },
    },
  },
  growthStrategy: {
    ...MOCK_STRATEGY_1.growthStrategy,
    core: {
      ...MOCK_STRATEGY_1.growthStrategy.core,
      northStarMetric: "Clinical Workflows Automated",
      growthLevers: [
        "HIPAA compliance",
        "EMR integrations",
        "Clinical efficiency",
      ],
    },
  },
};

// Clone mock strategy for Security focus
export const MOCK_STRATEGY_4 = {
  ...MOCK_STRATEGY_1,
  _id: "4",
  name: "Q4 2025 Security Focus",
  platforms: {
    ...MOCK_STRATEGY_1.platforms,
    xAds: {
      ...MOCK_STRATEGY_1.platforms.xAds,
      enabled: true,
      metrics: {
        budget: 7500,
        spend: 7000,
        impressions: 250000,
        clicks: 12500,
        conversions: 375,
        costPerClick: 0.56,
        costPerConversion: 18.67,
        conversionRate: 0.03,
      },
    },
  },
  crossPlatform: {
    ...MOCK_STRATEGY_1.crossPlatform,
    budgetAllocation: {
      ...MOCK_STRATEGY_1.crossPlatform.budgetAllocation,
      distribution: [
        {
          platform: "X",
          percentage: 30,
          reasoning: "Strong security and tech community engagement on X",
          kpis: [
            "Engagement Rate",
            "Lead Generation Rate",
            "Cost Per Engagement",
          ],
        },
        ...MOCK_STRATEGY_1.crossPlatform.budgetAllocation.distribution.slice(1),
      ],
    },
  },
  influencerMarketing: {
    ...MOCK_STRATEGY_1.influencerMarketing,
    strategy: {
      ...MOCK_STRATEGY_1.influencerMarketing.strategy,
      platformMix: ["LinkedIn", "Security blogs", "Cybersecurity forums"],
      campaignTypes: [
        "Security assessments",
        "Compliance webinars",
        "Risk management guides",
      ],
    },
  },
  affiliateMarketing: {
    ...MOCK_STRATEGY_1.affiliateMarketing,
    partnerTypes: {
      ...MOCK_STRATEGY_1.affiliateMarketing.partnerTypes,
      industryExperts: {
        niches: ["Cybersecurity", "Compliance", "IT Security"],
        qualifications: ["Security certifications", "Compliance expertise"],
        outreachPlan: ["Security conferences", "Compliance networks"],
      },
    },
  },
  growthStrategy: {
    ...MOCK_STRATEGY_1.growthStrategy,
    core: {
      ...MOCK_STRATEGY_1.growthStrategy.core,
      northStarMetric: "Security Compliance Score",
      growthLevers: [
        "Security certifications",
        "Compliance automation",
        "Risk management",
      ],
    },
  },
};
